// TeamsPhoneModelsManager — admin page at /settings/teams-phone-models.
// Condition B of the Teams-phone device-code suppression tier (v1.71.0): a global
// list of lowercased SUBSTRING patterns matched against a registered device's
// displayName (e.g. `polyccx` matches `PolyCCX600`). Read-open, admin-write.
// Soft-delete (enabled=false). Every write bumps a __version sentinel the pipeline
// polls. RowKey is server-derived from the pattern (spaces → '-'), kept 1:1.

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  MagnifyingGlassIcon,
  PlusIcon,
  TrashIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  DevicePhoneMobileIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  LockClosedIcon,
  ArrowsRightLeftIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { format, parseISO } from 'date-fns';
import {
  fetchTeamsPhoneModels,
  fetchTeamsPhoneModelsSentinelHealth,
  addTeamsPhoneModel,
  updateTeamsPhoneModel,
  removeTeamsPhoneModel,
  reconcileTeamsPhoneModelsSentinel,
} from '../api/client';
import { useAuth } from '../hooks/useAuth';
import Badge from './ui/Badge';
import Pagination from './Pagination';

function formatDate(dateStr) {
  if (!dateStr) return '—';
  try { return format(parseISO(dateStr), 'MMM d, yyyy'); } catch { return dateStr; }
}

// A single short bare word (no spaces/digits, ≤ 5 chars) is likely too broad and
// will over-suppress (e.g. `cisco`). Warn, don't block — the analyst may know best.
function isBroadToken(pattern) {
  const p = (pattern || '').trim();
  return p.length > 0 && p.length <= 5 && !/[\s\d]/.test(p);
}

export default function TeamsPhoneModelsManager() {
  const { user } = useAuth();
  const isAdmin = !!user?.isAdmin;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterQuery, setFilterQuery] = useState('');
  const [showDisabled, setShowDisabled] = useState(true);

  const [health, setHealth] = useState(null);
  const [reconciling, setReconciling] = useState(false);
  const [confirmReconcile, setConfirmReconcile] = useState(false);

  const [formPattern, setFormPattern] = useState('');
  const [formNotes, setFormNotes] = useState('');
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState(null);

  const [editingId, setEditingId] = useState(null);
  const [editNotes, setEditNotes] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState(null);

  const [confirmRemove, setConfirmRemove] = useState(null);
  const [removing, setRemoving] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  const [sortKey, setSortKey] = useState('pattern');
  const [sortDir, setSortDir] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Resizable columns: Pattern, Enabled, Notes, Added By, Date, Actions
  const [colWidths, setColWidths] = useState([260, 110, 320, 180, 130, 110]);
  const thRefs = useRef([]);
  const tableRef = useRef(null);

  const startResize = useCallback((e, colIdx) => {
    e.preventDefault();
    const th = thRefs.current[colIdx];
    const table = tableRef.current;
    if (!th) return;
    const startX = e.clientX;
    const startWidth = th.offsetWidth;
    const startTableWidth = table ? table.offsetWidth : 0;
    let lastWidth = startWidth;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    const onMouseMove = (moveEvt) => {
      lastWidth = Math.max(50, startWidth + moveEvt.clientX - startX);
      th.style.width = `${lastWidth}px`;
      if (table) table.style.width = `${startTableWidth + (lastWidth - startWidth)}px`;
    };
    const onMouseUp = () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      setColWidths(prev => { const n = [...prev]; n[colIdx] = lastWidth; return n; });
      if (table) table.style.width = '';
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, []);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchTeamsPhoneModels();
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to load Teams-phone models');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const loadHealth = async () => {
    try {
      const h = await fetchTeamsPhoneModelsSentinelHealth();
      setHealth(h);
    } catch { /* best-effort */ }
  };

  useEffect(() => {
    load();
    loadHealth();
    const intervalId = setInterval(loadHealth, 30_000);
    return () => clearInterval(intervalId);
  }, []);

  const filtered = useMemo(() => {
    let r = rows;
    if (!showDisabled) r = r.filter((x) => x.enabled !== false);
    if (filterQuery.trim()) {
      const q = filterQuery.toLowerCase();
      r = r.filter((x) =>
        (x.pattern && x.pattern.toLowerCase().includes(q)) ||
        (x.notes && x.notes.toLowerCase().includes(q)) ||
        (x.addedBy && x.addedBy.toLowerCase().includes(q))
      );
    }
    return r;
  }, [rows, showDisabled, filterQuery]);

  const sorted = useMemo(() => {
    const result = [...filtered];
    result.sort((a, b) => {
      const av = a[sortKey] ?? '';
      const bv = b[sortKey] ?? '';
      if (av === bv) return 0;
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      return sortDir === 'asc' ? 1 : -1;
    });
    return result;
  }, [filtered, sortKey, sortDir]);

  useEffect(() => { setCurrentPage(1); }, [filterQuery, showDisabled, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIdx = (safeCurrentPage - 1) * pageSize;
  const paged = sorted.slice(startIdx, startIdx + pageSize);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
  };

  const SortIndicator = ({ k }) => {
    if (sortKey !== k) return <span className="text-gray-700 ml-1">{'↕'}</span>;
    return sortDir === 'asc' ? <ChevronUpIcon className="inline h-3 w-3 ml-1" /> : <ChevronDownIcon className="inline h-3 w-3 ml-1" />;
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    const pattern = formPattern.trim().toLowerCase();
    if (!pattern) { setAddError('Pattern is required.'); return; }
    if (pattern.length > 256) { setAddError('Pattern exceeds 256 chars.'); return; }
    if (formNotes && formNotes.length > 2000) { setAddError('Notes exceeds 2000 chars.'); return; }

    setAdding(true);
    setAddError(null);
    try {
      const created = await addTeamsPhoneModel({ pattern, notes: formNotes.trim(), enabled: true });
      setRows((prev) => {
        const ix = prev.findIndex((r) => r.rowKey === created.rowKey);
        if (ix >= 0) { const next = [...prev]; next[ix] = created; return next; }
        return [created, ...prev];
      });
      setFormPattern('');
      setFormNotes('');
      loadHealth();
    } catch (err) {
      setAddError(err.message || 'Failed to add pattern');
    } finally {
      setAdding(false);
    }
  };

  const startEdit = (row) => {
    setEditError(null);
    setConfirmRemove(null);
    setEditingId(row.rowKey);
    setEditNotes(row.notes || '');
  };

  const cancelEdit = () => { setEditingId(null); setEditNotes(''); setEditError(null); };

  const handleSaveEdit = async (row) => {
    if (editNotes && editNotes.length > 2000) { setEditError('Notes exceeds 2000 chars.'); return; }
    setSavingEdit(true);
    setEditError(null);
    try {
      const updated = await updateTeamsPhoneModel(row.rowKey, { notes: editNotes.trim() });
      setRows((prev) => prev.map((x) => (x.rowKey === row.rowKey ? { ...x, ...updated } : x)));
      cancelEdit();
      loadHealth();
    } catch (err) {
      setEditError(err.message || 'Failed to save changes');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleToggleEnabled = async (row) => {
    setTogglingId(row.rowKey);
    try {
      const updated = await updateTeamsPhoneModel(row.rowKey, { enabled: !row.enabled });
      setRows((prev) => prev.map((x) => (x.rowKey === row.rowKey ? { ...x, ...updated } : x)));
      loadHealth();
    } catch (err) {
      setError(err.message || 'Failed to toggle pattern');
    } finally {
      setTogglingId(null);
    }
  };

  const handleRemove = async (row) => {
    setRemoving(true);
    try {
      await removeTeamsPhoneModel(row.rowKey);
      setRows((prev) => prev.map((x) => (x.rowKey === row.rowKey ? { ...x, enabled: false } : x)));
      setConfirmRemove(null);
      loadHealth();
    } catch (err) {
      setError(err.message || 'Failed to disable pattern');
    } finally {
      setRemoving(false);
    }
  };

  const handleReconcile = async () => {
    setReconciling(true);
    try {
      await reconcileTeamsPhoneModelsSentinel();
      setConfirmReconcile(false);
      loadHealth();
    } catch (err) {
      setError(err.message || 'Failed to reconcile sentinel');
    } finally {
      setReconciling(false);
    }
  };

  return (
    <div className="p-6 lg:p-8">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <DevicePhoneMobileIcon className="h-7 w-7 text-blue-500" />
          <h1 className="text-2xl font-bold text-gray-100">Teams-Phone Device Models</h1>
        </div>
        <p className="text-sm text-gray-500 max-w-3xl">
          Global lowercased <strong>substring</strong> patterns matched against a registered device's name to confirm a
          Microsoft-Teams-certified desk phone (Poly / Yealink / AudioCodes). This is Condition&nbsp;B of the device-code
          suppression tier — it only suppresses when an allowlisted account (Account Allowlist page) also registered a
          matching device within ~2&nbsp;minutes. Keep patterns <strong>specific</strong>: a bare vendor token like{' '}
          <span className="font-mono text-gray-400">cisco</span> over-suppresses.
        </p>
      </div>

      {health?.outboxPending && (
        <div className="mb-4 p-3 bg-amber-900/30 border border-amber-700/50 rounded-lg text-amber-300 text-sm flex items-center gap-2">
          <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
          <span>
            Pending sync since {formatDate(health.outboxEnqueuedUtc)} ({health.outboxAttemptCount} attempt{health.outboxAttemptCount !== 1 ? 's' : ''}).
            The detection pipeline may not yet see recent changes. The retry timer runs every ~30&nbsp;s.
          </span>
          {isAdmin && (
            <button onClick={() => setConfirmReconcile(true)} className="ml-auto underline hover:no-underline text-amber-200">
              Reconcile now
            </button>
          )}
        </div>
      )}

      {confirmReconcile && (
        <div className="mb-4 p-3 bg-gray-900 border border-gray-700 rounded-lg text-sm flex items-center gap-3">
          <ArrowsRightLeftIcon className="h-4 w-4 text-blue-400 flex-shrink-0" />
          <span className="text-gray-300">Force-bump the sentinel? This recomputes the version monotonically and clears any pending outbox row.</span>
          <button onClick={handleReconcile} disabled={reconciling} className="btn-primary px-3 py-1 text-xs ml-auto">
            {reconciling ? 'Reconciling…' : 'Confirm'}
          </button>
          <button onClick={() => setConfirmReconcile(false)} className="btn-secondary px-3 py-1 text-xs">Cancel</button>
        </div>
      )}

      {isAdmin ? (
        <div className="card mb-6">
          <h2 className="text-sm font-semibold text-gray-300 mb-4">Add Device-Name Pattern</h2>
          <form onSubmit={handleAdd} className="space-y-3">
            <div className="flex flex-col lg:flex-row gap-3 lg:items-end">
              <div className="flex-1 min-w-0">
                <label className="block text-xs text-gray-500 mb-1">Pattern (lowercased substring)</label>
                <input
                  type="text"
                  placeholder="e.g. polyccx, yealink mp, audiocodes"
                  value={formPattern}
                  onChange={(e) => setFormPattern(e.target.value)}
                  className="input-field h-10 py-0 font-mono text-xs"
                  required
                />
              </div>
              <div className="flex-1 min-w-0">
                <label className="block text-xs text-gray-500 mb-1">Notes</label>
                <input
                  type="text"
                  placeholder="e.g. Poly CCX desk-phone family"
                  value={formNotes}
                  onChange={(e) => setFormNotes(e.target.value)}
                  className="input-field h-10 py-0"
                />
              </div>
              <div className="flex-shrink-0">
                <button type="submit" disabled={adding || !formPattern.trim()} className="btn-primary flex items-center gap-2 whitespace-nowrap">
                  {adding ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <PlusIcon className="h-4 w-4" />}
                  Add Pattern
                </button>
              </div>
            </div>
            {isBroadToken(formPattern) && (
              <p className="text-xs text-amber-300/80 flex items-start gap-1.5">
                <ExclamationTriangleIcon className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                <span><span className="font-mono">{formPattern.trim().toLowerCase()}</span> looks like a broad single token — substring matching may over-suppress unrelated devices. Prefer a more specific model family.</span>
              </p>
            )}
            <p className="text-xs text-gray-600">
              The RowKey is derived from the pattern (spaces → <span className="font-mono">-</span>) and kept 1:1; two
              patterns that collapse to the same slug are rejected. Re-adding an existing pattern is an idempotent upsert.
            </p>
          </form>
          {addError && <p className="mt-2 text-sm text-red-400">{addError}</p>}
        </div>
      ) : (
        <div className="card mb-6 flex items-center gap-3 text-sm text-gray-400">
          <LockClosedIcon className="h-4 w-4 text-gray-500" />
          <span>Read-only view — admin access is required to add, edit, or disable patterns.</span>
        </div>
      )}

      {error && (
        <div className="mb-4 p-4 bg-red-900/30 border border-red-700/50 rounded-lg text-red-300 text-sm flex items-center gap-2">
          <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
          <button onClick={load} className="ml-auto underline hover:no-underline text-red-400">Retry</button>
        </div>
      )}

      {editError && (
        <div className="mb-4 p-3 bg-red-900/30 border border-red-700/50 rounded-lg text-red-300 text-sm flex items-center gap-2">
          <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
          <span>{editError}</span>
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div className="flex flex-col sm:flex-row gap-3 flex-1">
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
            <input
              type="text"
              placeholder="Filter by pattern, notes, added by..."
              value={filterQuery}
              onChange={(e) => setFilterQuery(e.target.value)}
              className="input-field pl-9"
            />
          </div>
          <label className="flex items-center gap-2 text-xs text-gray-400 cursor-pointer whitespace-nowrap">
            <input
              type="checkbox"
              checked={showDisabled}
              onChange={(e) => setShowDisabled(e.target.checked)}
              className="rounded border-gray-600 bg-gray-800 text-blue-500 focus:ring-blue-500"
            />
            Show disabled
          </label>
        </div>
        <div className="flex items-center gap-3 text-xs text-gray-500">
          {isAdmin && (
            <button onClick={() => setConfirmReconcile(true)} disabled={reconciling} className="btn-secondary flex items-center gap-1.5 text-xs" title="Force-bump the sentinel (recovery)">
              <ArrowsRightLeftIcon className="h-3.5 w-3.5" />
              Reconcile sentinel
            </button>
          )}
          <span>{filtered.length} pattern{filtered.length !== 1 ? 's' : ''}</span>
        </div>
      </div>

      <div className="card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table ref={tableRef} className="text-sm" style={{ tableLayout: 'fixed', width: `${colWidths.reduce((a, b) => a + b, 0)}px` }}>
            <thead>
              <tr className="border-b border-gray-800">
                {[
                  { label: 'Pattern',   sortKey: 'pattern' },
                  { label: 'Enabled',   sortKey: 'enabled' },
                  { label: 'Notes',     sortKey: null },
                  { label: 'Added By',  sortKey: 'addedBy' },
                  { label: 'Date',      sortKey: 'addedUtc' },
                  { label: 'Actions',   sortKey: null },
                ].map((col, colIdx) => (
                  <th
                    key={col.label}
                    ref={(el) => { thRefs.current[colIdx] = el; }}
                    onClick={col.sortKey ? () => toggleSort(col.sortKey) : undefined}
                    className={`${colIdx === 5 ? 'text-center' : 'text-left'} text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3 whitespace-nowrap relative select-none ${col.sortKey ? 'cursor-pointer hover:text-gray-300' : ''}`}
                    style={{ width: `${colWidths[colIdx]}px` }}
                  >
                    <span className="truncate">{col.label}</span>
                    {col.sortKey && <SortIndicator k={col.sortKey} />}
                    <div className="absolute top-0 right-0 h-full w-2 cursor-col-resize z-10 flex items-stretch justify-center group" onMouseDown={(e) => { e.stopPropagation(); startResize(e, colIdx); }}>
                      <div className="w-px bg-gray-700/50 group-hover:bg-blue-500/70 transition-colors" />
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800/50">
              {loading && (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <svg className="animate-spin h-8 w-8 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      <span className="text-gray-500 text-sm">Loading patterns...</span>
                    </div>
                  </td>
                </tr>
              )}

              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center">
                    <DevicePhoneMobileIcon className="h-10 w-10 text-gray-700 mx-auto mb-3" />
                    <p className="text-gray-400 font-medium">No device-name patterns</p>
                    <p className="text-gray-600 text-sm mt-1">
                      {filterQuery || !showDisabled ? 'Try adjusting your filters.' : isAdmin ? 'Add a pattern above to get started.' : 'No patterns yet.'}
                    </p>
                  </td>
                </tr>
              )}

              {!loading && paged.map((r) => {
                const isEditing = editingId === r.rowKey;
                const isEnabled = r.enabled !== false;
                return (
                <tr key={r.rowKey} className={`hover:bg-gray-800/50 transition-colors ${isEnabled ? '' : 'opacity-60'}`}>
                  <td className="px-4 py-3 font-mono text-xs text-gray-300 truncate overflow-hidden" title={r.pattern}>{r.pattern}</td>
                  <td className="px-4 py-3">
                    {isAdmin ? (
                      <button onClick={() => handleToggleEnabled(r)} disabled={togglingId === r.rowKey} title={isEnabled ? 'Click to disable' : 'Click to enable'} className="focus:outline-none">
                        <Badge tone={isEnabled ? 'success' : 'neutral'}>{isEnabled ? 'Enabled' : 'Disabled'}</Badge>
                      </button>
                    ) : (
                      <Badge tone={isEnabled ? 'success' : 'neutral'}>{isEnabled ? 'Enabled' : 'Disabled'}</Badge>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-400 truncate overflow-hidden" title={r.notes || ''}>
                    {isEditing ? (
                      <input type="text" value={editNotes} onChange={(e) => setEditNotes(e.target.value)} className="input-field w-full text-xs py-1" autoFocus />
                    ) : (
                      r.notes || '—'
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-400 text-xs truncate overflow-hidden" title={r.addedBy || ''}>{r.addedBy || '—'}</td>
                  <td className="px-4 py-3 text-gray-500 text-xs truncate overflow-hidden">{formatDate(r.addedUtc)}</td>
                  <td className="px-4 py-3 text-center">
                    {!isAdmin ? (
                      <span className="text-gray-700">{'—'}</span>
                    ) : isEditing ? (
                      <div className="flex items-center justify-center gap-2">
                        <button onClick={() => handleSaveEdit(r)} disabled={savingEdit} className="text-gray-600 hover:text-green-400 transition-colors p-1" title="Save">
                          {savingEdit ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <CheckIcon className="h-4 w-4" />}
                        </button>
                        <button onClick={cancelEdit} disabled={savingEdit} className="text-gray-600 hover:text-gray-300 transition-colors p-1" title="Cancel">
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      </div>
                    ) : confirmRemove === r.rowKey ? (
                      <div className="flex items-center justify-center gap-2">
                        <button onClick={() => handleRemove(r)} disabled={removing} className="btn-danger px-2 py-1 text-xs">
                          {removing ? 'Disabling...' : 'Confirm'}
                        </button>
                        <button onClick={() => setConfirmRemove(null)} className="btn-secondary px-2 py-1 text-xs">Cancel</button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => startEdit(r)} className="text-gray-600 hover:text-blue-400 transition-colors p-1" title="Edit notes (pattern is immutable — delete and re-add to change it)">
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button onClick={() => setConfirmRemove(r.rowKey)} disabled={!isEnabled} className="text-gray-600 hover:text-red-400 transition-colors p-1 disabled:opacity-30 disabled:cursor-not-allowed" title={isEnabled ? 'Disable (soft-delete)' : 'Already disabled'}>
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {!loading && filtered.length > 0 && (
          <Pagination
            currentPage={safeCurrentPage}
            totalPages={totalPages}
            pageSize={pageSize}
            totalItems={filtered.length}
            onPageChange={setCurrentPage}
            onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
          />
        )}
      </div>
    </div>
  );
}
